/**
 * Shared quota / market section constants used by:
 *   - src/pages/PendingMatches.js  (real matches + fictive clasico detail)
 *   - src/pages/Integrations.js    (per-integration clasico detail)
 *
 * Keeping a single source of truth ensures both screens render the
 * IDENTICAL set of markets, labels, group identifiers and field keys
 * — so per-market and per-group toggles look and behave the same.
 *
 * Conventions:
 *   - Group identifiers: snake_case (e.g. `ft_1x2`, `corners_ft_1x2`).
 *   - Field/quota keys:  camelCase  (e.g. `rh`, `htCornersBt1Yes`).
 *
 * NOTE: PendingMatches uses a flat `quotaConfig.<flagKey>Enabled` map for
 * per-quota toggling; Integrations uses a nested `marketConfig.<group>.<key>`
 * structure. The constants below describe the markets themselves; each
 * consumer maps them to its own backend shape.
 */

// Maps quota DTO field → flat `quotaConfig` enabled-flag key (used by PendingMatches).
export const QUOTA_CONFIG_MAP = {
  rh: 'r1Enabled', rd: 'rxEnabled', ra: 'r2Enabled',
  dc1x: 'dc1xEnabled', dc12: 'dc12Enabled', dcx2: 'dcx2Enabled',
  fgHome: 'fgHomeEnabled', fgNogoal: 'fgNogoalEnabled', fgAway: 'fgAwayEnabled',
  ou25o: 'ou25oEnabled', ou25u: 'ou25uEnabled',
  ou35o: 'ou35oEnabled', ou35u: 'ou35uEnabled',
  ou45o: 'ou45oEnabled', ou45u: 'ou45uEnabled',
  ou55o: 'ou55oEnabled', ou55u: 'ou55uEnabled',
  ou65o: 'ou65oEnabled', ou65u: 'ou65uEnabled',
  eh10r1: 'eh10r1Enabled', eh10rx: 'eh10rxEnabled', eh10r2: 'eh10r2Enabled',
  eh01r1: 'eh01r1Enabled', eh01rx: 'eh01rxEnabled', eh01r2: 'eh01r2Enabled',
  rhth: 'htR1Enabled', rhtd: 'htRxEnabled', rhta: 'htR2Enabled',
  htDc1x: 'htDc1xEnabled', htDc12: 'htDc12Enabled', htDcx2: 'htDcx2Enabled',
  htou05o: 'htOu05oEnabled', htou05u: 'htOu05uEnabled',
  htou15o: 'htOu15oEnabled', htou15u: 'htOu15uEnabled',
  htou25o: 'htOu25oEnabled', htou25u: 'htOu25uEnabled',
  htEh10r1: 'htEh10r1Enabled', htEh10rx: 'htEh10rxEnabled', htEh10r2: 'htEh10r2Enabled',
  htEh01r1: 'htEh01r1Enabled', htEh01rx: 'htEh01rxEnabled', htEh01r2: 'htEh01r2Enabled',
  htBttsYes: 'htBttsYesEnabled', htBttsNo: 'htBttsNoEnabled',
  // 2nd Half Goals
  shR1: 'shR1Enabled', shRx: 'shRxEnabled', shR2: 'shR2Enabled',
  shDc1x: 'shDc1xEnabled', shDc12: 'shDc12Enabled', shDcx2: 'shDcx2Enabled',
  shOu05o: 'shOu05oEnabled', shOu05u: 'shOu05uEnabled',
  shOu15o: 'shOu15oEnabled', shOu15u: 'shOu15uEnabled',
  shOu25o: 'shOu25oEnabled', shOu25u: 'shOu25uEnabled',
  shEh10r1: 'shEh10r1Enabled', shEh10rx: 'shEh10rxEnabled', shEh10r2: 'shEh10r2Enabled',
  shEh01r1: 'shEh01r1Enabled', shEh01rx: 'shEh01rxEnabled', shEh01r2: 'shEh01r2Enabled',
  shBttsYes: 'shBttsYesEnabled', shBttsNo: 'shBttsNoEnabled',
  // Special FT
  hshFirst: 'hshFirstEnabled', hshTie: 'hshTieEnabled', hshSecond: 'hshSecondEnabled',
  wtnHome: 'wtnHomeEnabled', wtnAway: 'wtnAwayEnabled',
  dnbHome: 'dnbHomeEnabled', dnbAway: 'dnbAwayEnabled',
  // Corners FT
  cornersR1: 'cornersR1Enabled', cornersRx: 'cornersRxEnabled', cornersR2: 'cornersR2Enabled',
  cornersDc1x: 'cornersDc1xEnabled', cornersDc12: 'cornersDc12Enabled', cornersDcx2: 'cornersDcx2Enabled',
  cornersOu1o: 'cornersOu1oEnabled', cornersOu1u: 'cornersOu1uEnabled',
  cornersOu2o: 'cornersOu2oEnabled', cornersOu2u: 'cornersOu2uEnabled',
  cornersOu3o: 'cornersOu3oEnabled', cornersOu3u: 'cornersOu3uEnabled',
  cornersEh10r1: 'cornersEh10r1Enabled', cornersEh10rx: 'cornersEh10rxEnabled', cornersEh10r2: 'cornersEh10r2Enabled',
  cornersEh01r1: 'cornersEh01r1Enabled', cornersEh01rx: 'cornersEh01rxEnabled', cornersEh01r2: 'cornersEh01r2Enabled',
  cornersEt3Yes: 'cornersEt3YesEnabled', cornersEt3No: 'cornersEt3NoEnabled',
  cornersEt4Yes: 'cornersEt4YesEnabled', cornersEt4No: 'cornersEt4NoEnabled',
  cornersEt5Yes: 'cornersEt5YesEnabled', cornersEt5No: 'cornersEt5NoEnabled',
  // Corners 1H
  htCornersR1: 'htCornersR1Enabled', htCornersRx: 'htCornersRxEnabled', htCornersR2: 'htCornersR2Enabled',
  htCornersDc1x: 'htCornersDc1xEnabled', htCornersDc12: 'htCornersDc12Enabled', htCornersDcx2: 'htCornersDcx2Enabled',
  htCornersOu1o: 'htCornersOu1oEnabled', htCornersOu1u: 'htCornersOu1uEnabled',
  htCornersOu2o: 'htCornersOu2oEnabled', htCornersOu2u: 'htCornersOu2uEnabled',
  htCornersOu3o: 'htCornersOu3oEnabled', htCornersOu3u: 'htCornersOu3uEnabled',
  htCornersEh10r1: 'htCornersEh10r1Enabled', htCornersEh10rx: 'htCornersEh10rxEnabled', htCornersEh10r2: 'htCornersEh10r2Enabled',
  htCornersEh01r1: 'htCornersEh01r1Enabled', htCornersEh01rx: 'htCornersEh01rxEnabled', htCornersEh01r2: 'htCornersEh01r2Enabled',
  htCornersBt1Yes: 'htCornersBt1YesEnabled', htCornersBt1No: 'htCornersBt1NoEnabled',
  htCornersBt2Yes: 'htCornersBt2YesEnabled', htCornersBt2No: 'htCornersBt2NoEnabled',
  htCornersBt3Yes: 'htCornersBt3YesEnabled', htCornersBt3No: 'htCornersBt3NoEnabled',
  // Yellow Cards FT
  ycR1: 'ycR1Enabled', ycRx: 'ycRxEnabled', ycR2: 'ycR2Enabled',
  ycDc1x: 'ycDc1xEnabled', ycDc12: 'ycDc12Enabled', ycDcx2: 'ycDcx2Enabled',
  ycOu1o: 'ycOu1oEnabled', ycOu1u: 'ycOu1uEnabled',
  ycOu2o: 'ycOu2oEnabled', ycOu2u: 'ycOu2uEnabled',
  ycOu3o: 'ycOu3oEnabled', ycOu3u: 'ycOu3uEnabled',
  ycEh10r1: 'ycEh10r1Enabled', ycEh10rx: 'ycEh10rxEnabled', ycEh10r2: 'ycEh10r2Enabled',
  ycEh01r1: 'ycEh01r1Enabled', ycEh01rx: 'ycEh01rxEnabled', ycEh01r2: 'ycEh01r2Enabled',
  ycEt1Yes: 'ycEt1YesEnabled', ycEt1No: 'ycEt1NoEnabled',
  ycEt2Yes: 'ycEt2YesEnabled', ycEt2No: 'ycEt2NoEnabled',
  ycEt3Yes: 'ycEt3YesEnabled', ycEt3No: 'ycEt3NoEnabled',
  // Yellow Cards 1H
  htYcR1: 'htYcR1Enabled', htYcRx: 'htYcRxEnabled', htYcR2: 'htYcR2Enabled',
  htYcDc1x: 'htYcDc1xEnabled', htYcDc12: 'htYcDc12Enabled', htYcDcx2: 'htYcDcx2Enabled',
  htYcOu1o: 'htYcOu1oEnabled', htYcOu1u: 'htYcOu1uEnabled',
  htYcOu2o: 'htYcOu2oEnabled', htYcOu2u: 'htYcOu2uEnabled',
  htYcOu3o: 'htYcOu3oEnabled', htYcOu3u: 'htYcOu3uEnabled',
  htYcEh10r1: 'htYcEh10r1Enabled', htYcEh10rx: 'htYcEh10rxEnabled', htYcEh10r2: 'htYcEh10r2Enabled',
  htYcEh01r1: 'htYcEh01r1Enabled', htYcEh01rx: 'htYcEh01rxEnabled', htYcEh01r2: 'htYcEh01r2Enabled',
  htYcEt1Yes: 'htYcEt1YesEnabled', htYcEt1No: 'htYcEt1NoEnabled',
  // SOT FT
  sotR1: 'sotR1Enabled', sotRx: 'sotRxEnabled', sotR2: 'sotR2Enabled',
  sotDc1x: 'sotDc1xEnabled', sotDc12: 'sotDc12Enabled', sotDcx2: 'sotDcx2Enabled',
  sotOu1o: 'sotOu1oEnabled', sotOu1u: 'sotOu1uEnabled',
  sotOu2o: 'sotOu2oEnabled', sotOu2u: 'sotOu2uEnabled',
  sotOu3o: 'sotOu3oEnabled', sotOu3u: 'sotOu3uEnabled',
  sotEh10r1: 'sotEh10r1Enabled', sotEh10rx: 'sotEh10rxEnabled', sotEh10r2: 'sotEh10r2Enabled',
  sotEh01r1: 'sotEh01r1Enabled', sotEh01rx: 'sotEh01rxEnabled', sotEh01r2: 'sotEh01r2Enabled',
  sotEt4Yes: 'sotEt4YesEnabled', sotEt4No: 'sotEt4NoEnabled',
  sotEt5Yes: 'sotEt5YesEnabled', sotEt5No: 'sotEt5NoEnabled',
  sotEt6Yes: 'sotEt6YesEnabled', sotEt6No: 'sotEt6NoEnabled',
  sotEt7Yes: 'sotEt7YesEnabled', sotEt7No: 'sotEt7NoEnabled',
  // SOT 1H
  htSotR1: 'htSotR1Enabled', htSotRx: 'htSotRxEnabled', htSotR2: 'htSotR2Enabled',
  htSotDc1x: 'htSotDc1xEnabled', htSotDc12: 'htSotDc12Enabled', htSotDcx2: 'htSotDcx2Enabled',
  htSotOu1o: 'htSotOu1oEnabled', htSotOu1u: 'htSotOu1uEnabled',
  htSotOu2o: 'htSotOu2oEnabled', htSotOu2u: 'htSotOu2uEnabled',
  htSotOu3o: 'htSotOu3oEnabled', htSotOu3u: 'htSotOu3uEnabled',
  htSotEh10r1: 'htSotEh10r1Enabled', htSotEh10rx: 'htSotEh10rxEnabled', htSotEh10r2: 'htSotEh10r2Enabled',
  htSotEh01r1: 'htSotEh01r1Enabled', htSotEh01rx: 'htSotEh01rxEnabled', htSotEh01r2: 'htSotEh01r2Enabled',
  htSotBt1Yes: 'htSotBt1YesEnabled', htSotBt1No: 'htSotBt1NoEnabled',
  htSotBt2Yes: 'htSotBt2YesEnabled', htSotBt2No: 'htSotBt2NoEnabled',
  htSotBt3Yes: 'htSotBt3YesEnabled', htSotBt3No: 'htSotBt3NoEnabled',
};

export const QUOTA_SECTIONS = [
  // `apiKey` is the canonical entity name accepted by the integrations markets API.
  // It is only set when it fundamentally differs from `key`; otherwise the backend's
  // case-insensitive matcher accepts `key` directly. See docs/FRONTEND_INTEGRATION_MARKET_TOGGLE.md.
  { label: 'FT — 1x2', group: 'ft_1x2', fields: [{ key: 'rh', apiKey: 'r1', l: 'Home' },{ key: 'rd', apiKey: 'rx', l: 'Draw' },{ key: 'ra', apiKey: 'r2', l: 'Away' }] },
  { label: 'FT — DC', group: 'ft_double_chance', fields: [{ key: 'dc1x', l: '1X' },{ key: 'dc12', l: '12' },{ key: 'dcx2', l: 'X2' }] },
  { label: 'First Goal', group: 'ft_first_goal', fields: [{ key: 'fgHome', l: 'Home' },{ key: 'fgNogoal', l: 'No Goal' },{ key: 'fgAway', l: 'Away' }] },
  { label: 'FT — O/U 2.5', group: 'ft_ou', fields: [{ key: 'ou25o', l: 'Over' },{ key: 'ou25u', l: 'Under' }] },
  { label: 'FT — O/U 3.5', group: 'ft_ou', fields: [{ key: 'ou35o', l: 'Over' },{ key: 'ou35u', l: 'Under' }] },
  { label: 'FT — O/U 4.5', group: 'ft_ou', fields: [{ key: 'ou45o', l: 'Over' },{ key: 'ou45u', l: 'Under' }] },
  { label: 'FT — O/U 5.5', group: 'ft_ou', fields: [{ key: 'ou55o', l: 'Over' },{ key: 'ou55u', l: 'Under' }] },
  { label: 'FT — O/U 6.5', group: 'ft_ou', fields: [{ key: 'ou65o', l: 'Over' },{ key: 'ou65u', l: 'Under' }] },
  { label: 'FT — EH', group: 'ft_eh', fields: [{ key: 'eh10r1', l: 'EH1:0-1' },{ key: 'eh10rx', l: 'EH1:0-X' },{ key: 'eh10r2', l: 'EH1:0-2' },{ key: 'eh01r1', l: 'EH0:1-1' },{ key: 'eh01rx', l: 'EH0:1-X' },{ key: 'eh01r2', l: 'EH0:1-2' }] },
  { label: 'HT — 1x2', group: 'ht_1x2', fields: [{ key: 'rhth', apiKey: 'htR1', l: 'Home' },{ key: 'rhtd', apiKey: 'htRx', l: 'Draw' },{ key: 'rhta', apiKey: 'htR2', l: 'Away' }] },
  { label: 'HT — DC', group: 'ht_double_chance', fields: [{ key: 'htDc1x', l: '1X' },{ key: 'htDc12', l: '12' },{ key: 'htDcx2', l: 'X2' }] },
  { label: 'HT — O/U 0.5', group: 'ht_ou', fields: [{ key: 'htou05o', l: 'Over' },{ key: 'htou05u', l: 'Under' }] },
  { label: 'HT — O/U 1.5', group: 'ht_ou', fields: [{ key: 'htou15o', l: 'Over' },{ key: 'htou15u', l: 'Under' }] },
  { label: 'HT — O/U 2.5', group: 'ht_ou', fields: [{ key: 'htou25o', l: 'Over' },{ key: 'htou25u', l: 'Under' }] },
  { label: 'HT — EH', group: 'ht_eh', fields: [{ key: 'htEh10r1', l: 'EH1:0-1' },{ key: 'htEh10rx', l: 'EH1:0-X' },{ key: 'htEh10r2', l: 'EH1:0-2' },{ key: 'htEh01r1', l: 'EH0:1-1' },{ key: 'htEh01rx', l: 'EH0:1-X' },{ key: 'htEh01r2', l: 'EH0:1-2' }] },
  { label: 'HT — BTTS', group: 'ht_btts', fields: [{ key: 'htBttsYes', l: 'Yes' },{ key: 'htBttsNo', l: 'No' }] },
  // 2nd Half Goals
  { label: '2H — 1x2', group: 'sh_1x2', fields: [{ key: 'shR1', l: 'Home' },{ key: 'shRx', l: 'Draw' },{ key: 'shR2', l: 'Away' }] },
  { label: '2H — DC', group: 'sh_double_chance', fields: [{ key: 'shDc1x', l: '1X' },{ key: 'shDc12', l: '12' },{ key: 'shDcx2', l: 'X2' }] },
  { label: '2H — O/U 0.5', group: 'sh_ou', fields: [{ key: 'shOu05o', l: 'Over' },{ key: 'shOu05u', l: 'Under' }] },
  { label: '2H — O/U 1.5', group: 'sh_ou', fields: [{ key: 'shOu15o', l: 'Over' },{ key: 'shOu15u', l: 'Under' }] },
  { label: '2H — O/U 2.5', group: 'sh_ou', fields: [{ key: 'shOu25o', l: 'Over' },{ key: 'shOu25u', l: 'Under' }] },
  { label: '2H — EH', group: 'sh_eh', fields: [{ key: 'shEh10r1', l: 'EH1:0-1' },{ key: 'shEh10rx', l: 'EH1:0-X' },{ key: 'shEh10r2', l: 'EH1:0-2' },{ key: 'shEh01r1', l: 'EH0:1-1' },{ key: 'shEh01rx', l: 'EH0:1-X' },{ key: 'shEh01r2', l: 'EH0:1-2' }] },
  { label: '2H — BTTS', group: 'sh_btts', fields: [{ key: 'shBttsYes', l: 'Yes' },{ key: 'shBttsNo', l: 'No' }] },
  // Special FT
  { label: 'Highest Scoring Half', group: 'hsh', fields: [{ key: 'hshFirst', l: 'First' },{ key: 'hshTie', l: 'Tie' },{ key: 'hshSecond', l: 'Second' }] },
  { label: 'Win to Nil', group: 'wtn', fields: [{ key: 'wtnHome', l: 'Home' },{ key: 'wtnAway', l: 'Away' }] },
  { label: 'Draw No Bet', group: 'dnb', fields: [{ key: 'dnbHome', l: 'Home' },{ key: 'dnbAway', l: 'Away' }] },
  // Corners FT
  { label: 'Corners FT — 1x2', group: 'corners_ft_1x2', fields: [{ key: 'cornersR1', l: 'Home' },{ key: 'cornersRx', l: 'Draw' },{ key: 'cornersR2', l: 'Away' }] },
  { label: 'Corners FT — DC', group: 'corners_ft_dc', fields: [{ key: 'cornersDc1x', l: '1X' },{ key: 'cornersDc12', l: '12' },{ key: 'cornersDcx2', l: 'X2' }] },
  { label: 'Corners FT — EH', group: 'corners_ft_eh', fields: [{ key: 'cornersEh10r1', l: 'EH1:0-1' },{ key: 'cornersEh10rx', l: 'EH1:0-X' },{ key: 'cornersEh10r2', l: 'EH1:0-2' },{ key: 'cornersEh01r1', l: 'EH0:1-1' },{ key: 'cornersEh01rx', l: 'EH0:1-X' },{ key: 'cornersEh01r2', l: 'EH0:1-2' }] },
  { label: 'Corners FT — ET', group: 'corners_ft_et', fields: [{ key: 'cornersEt3Yes', l: '3+ Yes' },{ key: 'cornersEt3No', l: '3+ No' },{ key: 'cornersEt4Yes', l: '4+ Yes' },{ key: 'cornersEt4No', l: '4+ No' },{ key: 'cornersEt5Yes', l: '5+ Yes' },{ key: 'cornersEt5No', l: '5+ No' }] },
  // Corners 1H
  { label: 'Corners 1H — 1x2', group: 'corners_ht_1x2', fields: [{ key: 'htCornersR1', l: 'Home' },{ key: 'htCornersRx', l: 'Draw' },{ key: 'htCornersR2', l: 'Away' }] },
  { label: 'Corners 1H — DC', group: 'corners_ht_dc', fields: [{ key: 'htCornersDc1x', l: '1X' },{ key: 'htCornersDc12', l: '12' },{ key: 'htCornersDcx2', l: 'X2' }] },
  { label: 'Corners 1H — EH', group: 'corners_ht_eh', fields: [{ key: 'htCornersEh10r1', l: 'EH1:0-1' },{ key: 'htCornersEh10rx', l: 'EH1:0-X' },{ key: 'htCornersEh10r2', l: 'EH1:0-2' },{ key: 'htCornersEh01r1', l: 'EH0:1-1' },{ key: 'htCornersEh01rx', l: 'EH0:1-X' },{ key: 'htCornersEh01r2', l: 'EH0:1-2' }] },
  { label: 'Corners 1H — BT', group: 'corners_ht_bt', fields: [{ key: 'htCornersBt1Yes', l: '1+ Yes' },{ key: 'htCornersBt1No', l: '1+ No' },{ key: 'htCornersBt2Yes', l: '2+ Yes' },{ key: 'htCornersBt2No', l: '2+ No' },{ key: 'htCornersBt3Yes', l: '3+ Yes' },{ key: 'htCornersBt3No', l: '3+ No' }] },
  // Yellow Cards FT
  { label: 'YC FT — 1x2', group: 'yc_ft_1x2', fields: [{ key: 'ycR1', l: 'Home' },{ key: 'ycRx', l: 'Draw' },{ key: 'ycR2', l: 'Away' }] },
  { label: 'YC FT — DC', group: 'yc_ft_dc', fields: [{ key: 'ycDc1x', l: '1X' },{ key: 'ycDc12', l: '12' },{ key: 'ycDcx2', l: 'X2' }] },
  { label: 'YC FT — EH', group: 'yc_ft_eh', fields: [{ key: 'ycEh10r1', l: 'EH1:0-1' },{ key: 'ycEh10rx', l: 'EH1:0-X' },{ key: 'ycEh10r2', l: 'EH1:0-2' },{ key: 'ycEh01r1', l: 'EH0:1-1' },{ key: 'ycEh01rx', l: 'EH0:1-X' },{ key: 'ycEh01r2', l: 'EH0:1-2' }] },
  { label: 'YC FT — ET', group: 'yc_ft_et', fields: [{ key: 'ycEt1Yes', l: '1+ Yes' },{ key: 'ycEt1No', l: '1+ No' },{ key: 'ycEt2Yes', l: '2+ Yes' },{ key: 'ycEt2No', l: '2+ No' },{ key: 'ycEt3Yes', l: '3+ Yes' },{ key: 'ycEt3No', l: '3+ No' }] },
  // Yellow Cards 1H
  { label: 'YC 1H — 1x2', group: 'yc_ht_1x2', fields: [{ key: 'htYcR1', l: 'Home' },{ key: 'htYcRx', l: 'Draw' },{ key: 'htYcR2', l: 'Away' }] },
  { label: 'YC 1H — DC', group: 'yc_ht_dc', fields: [{ key: 'htYcDc1x', l: '1X' },{ key: 'htYcDc12', l: '12' },{ key: 'htYcDcx2', l: 'X2' }] },
  { label: 'YC 1H — EH', group: 'yc_ht_eh', fields: [{ key: 'htYcEh10r1', l: 'EH1:0-1' },{ key: 'htYcEh10rx', l: 'EH1:0-X' },{ key: 'htYcEh10r2', l: 'EH1:0-2' },{ key: 'htYcEh01r1', l: 'EH0:1-1' },{ key: 'htYcEh01rx', l: 'EH0:1-X' },{ key: 'htYcEh01r2', l: 'EH0:1-2' }] },
  { label: 'YC 1H — ET', group: 'yc_ht_et', fields: [{ key: 'htYcEt1Yes', l: '1+ Yes' },{ key: 'htYcEt1No', l: '1+ No' }] },
  // SOT FT
  { label: 'SOT FT — 1x2', group: 'sot_ft_1x2', fields: [{ key: 'sotR1', l: 'Home' },{ key: 'sotRx', l: 'Draw' },{ key: 'sotR2', l: 'Away' }] },
  { label: 'SOT FT — DC', group: 'sot_ft_dc', fields: [{ key: 'sotDc1x', l: '1X' },{ key: 'sotDc12', l: '12' },{ key: 'sotDcx2', l: 'X2' }] },
  { label: 'SOT FT — EH', group: 'sot_ft_eh', fields: [{ key: 'sotEh10r1', l: 'EH1:0-1' },{ key: 'sotEh10rx', l: 'EH1:0-X' },{ key: 'sotEh10r2', l: 'EH1:0-2' },{ key: 'sotEh01r1', l: 'EH0:1-1' },{ key: 'sotEh01rx', l: 'EH0:1-X' },{ key: 'sotEh01r2', l: 'EH0:1-2' }] },
  { label: 'SOT FT — ET', group: 'sot_ft_et', fields: [{ key: 'sotEt4Yes', l: '4+ Yes' },{ key: 'sotEt4No', l: '4+ No' },{ key: 'sotEt5Yes', l: '5+ Yes' },{ key: 'sotEt5No', l: '5+ No' },{ key: 'sotEt6Yes', l: '6+ Yes' },{ key: 'sotEt6No', l: '6+ No' },{ key: 'sotEt7Yes', l: '7+ Yes' },{ key: 'sotEt7No', l: '7+ No' }] },
  // SOT 1H
  { label: 'SOT 1H — 1x2', group: 'sot_ht_1x2', fields: [{ key: 'htSotR1', l: 'Home' },{ key: 'htSotRx', l: 'Draw' },{ key: 'htSotR2', l: 'Away' }] },
  { label: 'SOT 1H — DC', group: 'sot_ht_dc', fields: [{ key: 'htSotDc1x', l: '1X' },{ key: 'htSotDc12', l: '12' },{ key: 'htSotDcx2', l: 'X2' }] },
  { label: 'SOT 1H — EH', group: 'sot_ht_eh', fields: [{ key: 'htSotEh10r1', l: 'EH1:0-1' },{ key: 'htSotEh10rx', l: 'EH1:0-X' },{ key: 'htSotEh10r2', l: 'EH1:0-2' },{ key: 'htSotEh01r1', l: 'EH0:1-1' },{ key: 'htSotEh01rx', l: 'EH0:1-X' },{ key: 'htSotEh01r2', l: 'EH0:1-2' }] },
  { label: 'SOT 1H — BT', group: 'sot_ht_bt', fields: [{ key: 'htSotBt1Yes', l: '1+ Yes' },{ key: 'htSotBt1No', l: '1+ No' },{ key: 'htSotBt2Yes', l: '2+ Yes' },{ key: 'htSotBt2No', l: '2+ No' },{ key: 'htSotBt3Yes', l: '3+ Yes' },{ key: 'htSotBt3No', l: '3+ No' }] },
];

// Per-team total markets (split into FT/HT/2H groups). `apiKey` is the canonical
// entity name accepted by the integrations team-markets endpoint.
export const TT_FIELDS = [
  { key: 'ttft15o', apiKey: 'tt15o',   l: 'FT TT 1.5 O', group: 'tt_ft' },{ key: 'ttft15u', apiKey: 'tt15u',   l: 'FT TT 1.5 U', group: 'tt_ft' },
  { key: 'ttht05o', apiKey: 'ttHt05o', l: 'HT TT 0.5 O', group: 'tt_ht' },{ key: 'ttht05u', apiKey: 'ttHt05u', l: 'HT TT 0.5 U', group: 'tt_ht' },
  { key: 'ttsh05o', apiKey: 'ttSh05o', l: '2H TT 0.5 O', group: 'tt_sh' },{ key: 'ttsh05u', apiKey: 'ttSh05u', l: '2H TT 0.5 U', group: 'tt_sh' },
];

// Per-team statistic totals (Corners/YC/SOT TT) — line-based markets.
export const STAT_TT_FIELDS = [
  { lineKey: 'cornersTtLine', oKey: 'cornersTtO', uKey: 'cornersTtU', l: 'Corners TT FT', group: 'corners_tt_ft' },
  { lineKey: 'htCornersTtLine', oKey: 'htCornersTtO', uKey: 'htCornersTtU', l: 'Corners TT 1H', group: 'corners_tt_ht' },
  { lineKey: 'ycTtLine', oKey: 'ycTtO', uKey: 'ycTtU', l: 'YC TT FT', group: 'yc_tt_ft' },
  { lineKey: 'htYcTtLine', oKey: 'htYcTtO', uKey: 'htYcTtU', l: 'YC TT 1H', group: 'yc_tt_ht' },
  { lineKey: 'sotTtLine', oKey: 'sotTtO', uKey: 'sotTtU', l: 'SOT TT FT', group: 'sot_tt_ft' },
  { lineKey: 'htSotTtLine', oKey: 'htSotTtO', uKey: 'htSotTtU', l: 'SOT TT 1H', group: 'sot_tt_ht' },
];

// Dynamic O/U sections built at render time from quota DTO.
export function buildDynamicOuSections(quotaObj) {
  if (!quotaObj) return [];
  const sections = [];
  const cats = [
    { prefix: 'cornersOu', label: 'Corners FT', group: 'corners_ft_ou', count: 3 },
    { prefix: 'htCornersOu', label: 'Corners 1H', group: 'corners_ht_ou', count: 3 },
    { prefix: 'ycOu', label: 'YC FT', group: 'yc_ft_ou', count: 3 },
    { prefix: 'htYcOu', label: 'YC 1H', group: 'yc_ht_ou', count: 3 },
    { prefix: 'sotOu', label: 'SOT FT', group: 'sot_ft_ou', count: 3 },
    { prefix: 'htSotOu', label: 'SOT 1H', group: 'sot_ht_ou', count: 3 },
  ];
  for (const { prefix, label, group, count } of cats) {
    for (let n = 1; n <= count; n++) {
      const lineKey = `${prefix}${n}Line`;
      const lineVal = quotaObj[lineKey];
      if (lineVal == null) continue;
      sections.push({
        label: `${label} — O/U ${lineVal}`,
        group,
        fields: [
          { key: `${prefix}${n}o`, l: 'Over' },
          { key: `${prefix}${n}u`, l: 'Under' },
        ],
      });
    }
  }
  return sections;
}

// camelCase → snake_case (e.g. `htR1` → `ht_r1`).
function toSnake(s) {
  return s.replace(/([a-z0-9])([A-Z])/g, '$1_$2').replace(/([A-Z])([A-Z][a-z])/g, '$1_$2').toLowerCase();
}

// Returns the canonical entity name for the integrations markets API.
// Falls back to the quota DTO key when no override is set (backend matching is case-insensitive).
export function apiKeyOf(field) {
  return field.apiKey || field.key;
}

// Resolve current per-market enabled state inside a marketConfig group object.
// `field` can be either a field object `{key, apiKey?}` or a plain string key.
// The backend may store the flag under several naming conventions
// (`r1`, `r1_enabled`, `r1Enabled`, plus the legacy DTO key) — try them all
// before falling back to the default ON.
export function isMarketKeyOn(groupObj, field) {
  if (groupObj == null) return true;
  if (typeof groupObj === 'boolean') return groupObj;
  const apiKey = typeof field === 'string' ? field : apiKeyOf(field);
  const dtoKey  = typeof field === 'string' ? field : field.key;
  const candidates = [
    apiKey,
    apiKey.toLowerCase(),
    `${toSnake(apiKey)}_enabled`,
    `${apiKey}Enabled`,
  ];
  if (dtoKey !== apiKey) {
    candidates.push(dtoKey, dtoKey.toLowerCase(), `${dtoKey}Enabled`, `${toSnake(dtoKey)}_enabled`);
  }
  for (const c of candidates) {
    if (c in groupObj) return !!groupObj[c];
  }
  return true; // default ON when not present
}
